import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, FESTIVAL_META, resolveMediaUrl } from "@/lib/api";
import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ArtistSearch from "@/components/ArtistSearch";
import { toast } from "sonner";
import { Trash2, Plus, Upload } from "lucide-react";
import SeasonPanel from "@/pages/SeasonPanel";
import MailCenter from "@/pages/MailCenter";

export default function Admin() {
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="label-eyebrow">Kontrolcenter</div>
      <h1 className="hero-title text-4xl sm:text-5xl mt-2">Admin</h1>

      <Tabs defaultValue="season" className="mt-10">
        <TabsList className="bg-transparent border border-white/10 rounded-full p-1 flex-wrap h-auto">
          <TabsTrigger value="season" data-testid="admin-tab-season" className="rounded-full data-[state=active]:bg-white data-[state=active]:text-black">Sæson</TabsTrigger>
          <TabsTrigger value="results" data-testid="admin-tab-results" className="rounded-full data-[state=active]:bg-white data-[state=active]:text-black">Resultater</TabsTrigger>
          <TabsTrigger value="artists" data-testid="admin-tab-artists" className="rounded-full data-[state=active]:bg-white data-[state=active]:text-black">Kunstnere</TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users" className="rounded-full data-[state=active]:bg-white data-[state=active]:text-black">Brugere</TabsTrigger>
          <TabsTrigger value="images" data-testid="admin-tab-images" className="rounded-full data-[state=active]:bg-white data-[state=active]:text-black">Festival billeder</TabsTrigger>
          <TabsTrigger value="mail" data-testid="admin-tab-mail" className="rounded-full data-[state=active]:bg-white data-[state=active]:text-black">Mailcenter</TabsTrigger>
          <TabsTrigger value="settings" data-testid="admin-tab-settings" className="rounded-full data-[state=active]:bg-white data-[state=active]:text-black">Indstillinger</TabsTrigger>
        </TabsList>
        <TabsContent value="season" className="mt-8"><SeasonPanel /></TabsContent>
        <TabsContent value="results" className="mt-8"><ResultsPanel /></TabsContent>
        <TabsContent value="artists" className="mt-8"><ArtistsPanel /></TabsContent>
        <TabsContent value="users" className="mt-8"><UsersPanel /></TabsContent>
        <TabsContent value="images" className="mt-8"><FestivalImagesPanel /></TabsContent>
        <TabsContent value="mail" className="mt-8"><MailCenter /></TabsContent>
        <TabsContent value="settings" className="mt-8"><SettingsPanel /></TabsContent>
      </Tabs>
    </div>
  );
}

function ResultsPanel() {
  const qc = useQueryClient();
  const { data: results } = useQuery({ queryKey: ["results"], queryFn: () => api.get("/results").then(r => r.data) });
  const [festivalId, setFestivalId] = useState("roskilde");
  const [artist, setArtist] = useState(null);
  const [category, setCategory] = useState("international");

  const submit = async () => {
    if (!artist) return toast.error("Vælg en kunstner");
    try {
      await api.post("/results", {
        festival_id: festivalId, artist_id: artist.artist_id, category,
        is_headliner: false, is_orange_opener: false,
      });
      toast.success(`${artist.name} bekræftet på ${FESTIVAL_META[festivalId].name}`);
      setArtist(null);
      qc.invalidateQueries();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fejl"); }
  };

  const toggleHeadliner = async (r) => {
    try { await api.patch(`/results/${r.result_id}`, { is_headliner: !r.is_headliner }); qc.invalidateQueries(); } catch { toast.error("Fejl"); }
  };
  const toggleOrange = async (r) => {
    try { await api.patch(`/results/${r.result_id}`, { is_orange_opener: !r.is_orange_opener }); qc.invalidateQueries(); } catch { toast.error("Fejl"); }
  };

  const del = async (id) => {
    if (!window.confirm("Slet dette resultat?")) return;
    await api.delete(`/results/${id}`);
    toast.success("Slettet");
    qc.invalidateQueries();
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="card-flat p-6">
        <h3 className="font-display font-bold text-xl mb-4">Bekræft en artist</h3>
        <div className="space-y-4">
          <div>
            <Label className="label-eyebrow">Festival</Label>
            <Select value={festivalId} onValueChange={setFestivalId}>
              <SelectTrigger data-testid="admin-result-festival"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(FESTIVAL_META).map(([id, m]) => (
                  <SelectItem key={id} value={id}>{m.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-eyebrow">Kunstner</Label>
            {artist ? (
              <div className="card-flat p-3 flex justify-between items-center">
                <div>{artist.name} <span className="text-xs text-zinc-500">({artist.is_danish ? "DK" : "INT"})</span></div>
                <button onClick={() => setArtist(null)} className="text-zinc-500 hover:text-red-400 text-sm">Skift</button>
              </div>
            ) : (
              <ArtistSearch onSelect={setArtist} testId="admin-result-artist" placeholder="Søg kunstner…" />
            )}
          </div>
          <div>
            <Label className="label-eyebrow">Kunstner er</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="admin-result-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="international">International</SelectItem>
                <SelectItem value="danish">Dansk</SelectItem>
              </SelectContent>
            </Select>
            <div className="text-xs text-zinc-500 mt-1">Headliner-status og Orange åbner sætter du på listen når det bliver kendt.</div>
          </div>
          <Button onClick={submit} data-testid="admin-result-submit" className="bg-white text-black hover:bg-zinc-200 w-full">
            <Plus size={14} className="mr-1" /> Bekræft artist
          </Button>
        </div>
      </div>

      <div className="card-flat p-6">
        <h3 className="font-display font-bold text-xl mb-4">Bekræftede resultater ({results?.length ?? 0})</h3>
        <div className="text-xs text-zinc-500 mb-3">Marker headliner / Orange Scene åbner når du ved det. Point opdateres automatisk.</div>
        <div className="space-y-2 max-h-[600px] overflow-y-auto">
          {results?.map(r => (
            <div key={r.result_id} className="py-2 border-b border-white/5" data-testid={`admin-result-row-${r.result_id}`}>
              <div className="flex justify-between items-start gap-2">
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{r.artist_name}</div>
                  <div className="text-xs text-zinc-500">{FESTIVAL_META[r.festival_id]?.name} · {r.category}</div>
                </div>
                <button onClick={() => del(r.result_id)} className="text-zinc-500 hover:text-red-400 mt-1"><Trash2 size={14} /></button>
              </div>
              <div className="mt-2 flex flex-wrap gap-3">
                <label className="flex items-center gap-2 text-xs cursor-pointer">
                  <Checkbox checked={!!r.is_headliner} onCheckedChange={() => toggleHeadliner(r)} data-testid={`admin-toggle-headliner-${r.result_id}`} />
                  <span className={r.is_headliner ? "text-orange-300 font-semibold" : "text-zinc-500"}>👑 Headliner</span>
                </label>
                {r.festival_id === "roskilde" && (
                  <label className="flex items-center gap-2 text-xs cursor-pointer">
                    <Checkbox checked={!!r.is_orange_opener} onCheckedChange={() => toggleOrange(r)} data-testid={`admin-toggle-orange-${r.result_id}`} />
                    <span className={r.is_orange_opener ? "text-orange-500 font-semibold" : "text-zinc-500"}>🟠 Orange åbner</span>
                  </label>
                )}
              </div>
            </div>
          ))}
          {(!results || results.length === 0) && <div className="text-zinc-500 text-sm">Ingen resultater endnu.</div>}
        </div>
      </div>
    </div>
  );
}

function ArtistsPanel() {
  const qc = useQueryClient();
  const { data: artists } = useQuery({ queryKey: ["all-artists"], queryFn: () => api.get("/artists", { params: { limit: 5000 } }).then(r => r.data) });
  const [name, setName] = useState("");
  const [isDanish, setIsDanish] = useState(false);
  const [q, setQ] = useState("");

  const create = async () => {
    if (!name.trim()) return;
    try {
      await api.post("/artists", { name: name.trim(), is_danish: isDanish });
      toast.success("Tilføjet");
      setName(""); setIsDanish(false);
      qc.invalidateQueries({ queryKey: ["all-artists"] });
    } catch { toast.error("Fejl"); }
  };
  const del = async (id) => {
    if (!window.confirm("Slet denne kunstner?")) return;
    await api.delete(`/artists/${id}`);
    qc.invalidateQueries();
  };

  const filtered = (artists || []).filter(a => !q || a.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="card-flat p-6">
        <h3 className="font-display font-bold text-xl mb-4">Ny kunstner</h3>
        <div className="space-y-3">
          <Input placeholder="Kunstner navn" value={name} onChange={e => setName(e.target.value)} data-testid="admin-artist-name" />
          <div className="flex items-center gap-2">
            <Checkbox id="dk" checked={isDanish} onCheckedChange={setIsDanish} data-testid="admin-artist-danish" />
            <Label htmlFor="dk">Dansk kunstner</Label>
          </div>
          <Button onClick={create} data-testid="admin-artist-create" className="w-full bg-white text-black hover:bg-zinc-200">Tilføj</Button>
        </div>
      </div>
      <div className="card-flat p-6 lg:col-span-2">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-display font-bold text-xl">Alle kunstnere ({artists?.length ?? 0})</h3>
          <Input placeholder="Filtrér…" value={q} onChange={e => setQ(e.target.value)} className="max-w-[200px]" data-testid="admin-artist-filter" />
        </div>
        <div className="max-h-[500px] overflow-y-auto space-y-1">
          {filtered.slice(0, 200).map(a => (
            <div key={a.artist_id} className="flex justify-between items-center py-2 border-b border-white/5">
              <div>{a.name} <span className={`ml-2 text-xs px-2 py-0.5 rounded ${a.is_danish ? "bg-red-500/10 text-red-300" : "bg-blue-500/10 text-blue-300"}`}>{a.is_danish ? "DK" : "INT"}</span></div>
              <button onClick={() => del(a.artist_id)} className="text-zinc-500 hover:text-red-400"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function UsersPanel() {
  const qc = useQueryClient();
  const { data: users } = useQuery({ queryKey: ["admin-users"], queryFn: () => api.get("/admin/users").then(r => r.data) });
  const [showDisabled, setShowDisabled] = useState(false);

  const toggleDisabled = async (u) => {
    await api.patch(`/admin/users/${u.user_id}`, { disabled: !u.disabled });
    qc.invalidateQueries({ queryKey: ["admin-users"] });
    qc.invalidateQueries({ queryKey: ["lb-overall"] });
    qc.invalidateQueries({ queryKey: ["users"] });
    toast.success(u.disabled ? "Bruger aktiveret" : "Bruger deaktiveret – skjules straks fra leaderboardet");
  };
  const toggleAdmin = async (u) => {
    await api.patch(`/admin/users/${u.user_id}`, { is_admin: !u.is_admin });
    qc.invalidateQueries({ queryKey: ["admin-users"] });
  };

  const active = (users || []).filter(u => !u.disabled);
  const disabled = (users || []).filter(u => u.disabled);
  const list = showDisabled ? disabled : active;

  return (
    <div className="card-flat p-6">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <h3 className="font-display font-bold text-xl">Brugere ({active.length} aktive · {disabled.length} deaktiveret)</h3>
        <div className="flex gap-2">
          <Button variant={showDisabled ? "ghost" : "outline"} size="sm" onClick={() => setShowDisabled(false)} data-testid="admin-users-tab-active">Aktive</Button>
          <Button variant={showDisabled ? "outline" : "ghost"} size="sm" onClick={() => setShowDisabled(true)} data-testid="admin-users-tab-disabled">Deaktiveret</Button>
        </div>
      </div>
      <div className="space-y-2">
        {list.map(u => (
          <div key={u.user_id} className="flex flex-wrap items-center justify-between gap-3 py-3 border-b border-white/5" data-testid={`admin-user-${u.user_id}`}>
            <a
              href={`/u/${u.user_id}`}
              target="_blank" rel="noopener noreferrer"
              className="min-w-0 flex-1 hover:opacity-80"
              data-testid={`admin-user-open-${u.user_id}`}
            >
              <div className="font-medium truncate">
                {u.display_name || u.name}
                {u.is_admin && <span className="text-xs text-orange-400 ml-2">ADMIN</span>}
                {u.disabled && <span className="text-xs text-red-400 ml-2">DEAKTIVERET</span>}
              </div>
              <div className="text-xs text-zinc-500 truncate">{u.email}</div>
            </a>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => toggleAdmin(u)}>{u.is_admin ? "Fjern admin" : "Gør admin"}</Button>
              <Button variant="outline" size="sm" onClick={() => toggleDisabled(u)} data-testid={`admin-user-toggle-${u.user_id}`}>{u.disabled ? "Aktivér" : "Deaktivér"}</Button>
            </div>
          </div>
        ))}
        {list.length === 0 && (
          <div className="text-sm text-zinc-500 py-6 text-center">Ingen brugere i denne visning.</div>
        )}
      </div>
    </div>
  );
}

function FestivalImagesPanel() {
  const qc = useQueryClient();
  const { data: festData } = useQuery({ queryKey: ["festivals"], queryFn: () => api.get("/festivals").then(r => r.data) });
  const [uploading, setUploading] = useState(null);
  const inputs = useRef({});

  const upload = async (festivalId, file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Kun billeder"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Max 5MB"); return; }
    setUploading(festivalId);
    try {
      const form = new FormData();
      form.append("file", file);
      await api.post(`/upload/festival-image/${festivalId}`, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Billede opdateret");
      qc.invalidateQueries({ queryKey: ["festivals"] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload fejlede");
    } finally { setUploading(null); }
  };

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      {festData?.festivals.map(f => {
        const meta = FESTIVAL_META[f.id];
        const preview = resolveMediaUrl(f.image_url) || meta.image;
        return (
          <div key={f.id} className="card-flat overflow-hidden" data-testid={`admin-fest-img-${f.id}`}>
            <div className="aspect-[16/10] relative">
              <img src={preview} alt="" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 40%, rgba(0,0,0,0.9))" }} />
              <div className="absolute inset-x-0 bottom-0 p-4">
                <div className="h-1 w-10 mb-2" style={{ background: meta.color }} />
                <div className="font-display font-bold text-lg">{f.name}</div>
              </div>
            </div>
            <div className="p-4">
              <input ref={el => inputs.current[f.id] = el} type="file" accept="image/*" className="hidden"
                onChange={(e) => upload(f.id, e.target.files?.[0])} data-testid={`admin-fest-img-file-${f.id}`} />
              <Button variant="outline" className="w-full" disabled={uploading === f.id}
                onClick={() => inputs.current[f.id]?.click()} data-testid={`admin-fest-img-upload-${f.id}`}>
                <Upload size={14} className="mr-2" /> {uploading === f.id ? "Uploader…" : (f.image_url ? "Skift billede" : "Upload billede")}
              </Button>
              <div className="text-xs text-zinc-500 mt-2">Anbefalet: bredt landskabsbillede, max 5MB.</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function SettingsPanel() {
  const qc = useQueryClient();
  const { data: festData } = useQuery({ queryKey: ["festivals"], queryFn: () => api.get("/festivals").then(r => r.data) });
  const [deadline, setDeadline] = useState("");

  const save = async () => {
    if (!deadline) return;
    try {
      const iso = new Date(deadline).toISOString();
      await api.patch("/admin/deadline", { deadline: iso });
      toast.success("Deadline opdateret");
      qc.invalidateQueries();
    } catch (e) { toast.error("Fejl"); }
  };

  const currentIso = festData?.deadline ? new Date(festData.deadline).toISOString().slice(0, 16) : "";

  return (
    <div className="card-flat p-6 max-w-lg">
      <h3 className="font-display font-bold text-xl mb-4">Deadline</h3>
      <div className="text-sm text-zinc-400 mb-3">Nuværende: <span className="text-white">{festData?.deadline ? new Date(festData.deadline).toLocaleString("da-DK") : "–"}</span></div>
      <Input type="datetime-local" value={deadline || currentIso} onChange={e => setDeadline(e.target.value)} data-testid="admin-deadline-input" />
      <Button onClick={save} className="mt-4 bg-white text-black hover:bg-zinc-200" data-testid="admin-deadline-save">Gem deadline</Button>
    </div>
  );
}
